import { CampaignMonitorResponse, ContractView, MonitorFilter, MonitorOutcomes, QueueRow, TypeStateDisplay, WaitingReason } from "./monitor.types";

/**
 * why: o mesmo número de `MANUAL_SENDS_PER_DAY` (backend, `campaigns/resend/build-manual-send-sql.ts`),
 * contado contra `manualResendsToday`, que o backend apura pela mesma regra do 429 (R5). Mudar lá é mudar aqui.
 */
export const MAX_MANUAL_RESENDS_PER_DAY = 3;

const SP = "America/Sao_Paulo";
const DDMM = new Intl.DateTimeFormat("pt-BR", { timeZone: SP, day: "2-digit", month: "2-digit" });
const HHMM = new Intl.DateTimeFormat("pt-BR", { timeZone: SP, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

export const ddmm = (iso: string): string => DDMM.format(new Date(iso));
export const ddmmHHmm = (iso: string): string => `${ddmm(iso)} ${HHMM.format(new Date(iso))}`;

/** invariant: uma linha por titular e por contrato; o mesmo CPF em dois contratos são duas linhas. */
export const rowKey = (r: Pick<QueueRow, "workspaceId" | "taxPayerId">): string => `${r.workspaceId}:${r.taxPayerId}`;

/** why: textos da tabela de estados da spec de 29/09 §5.3; `lastRoundIdle` vem do contrato. */
export function typeStateText(s: TypeStateDisplay, lastRoundIdle = false): { text: string; alert: boolean } {
  if (s.kind === "OFF") return { text: "Campanha desligada", alert: false };
  if (s.kind === "ARMED") {
    const days = s.armedDays === null ? "" : ` há ${s.armedDays} ${s.armedDays === 1 ? "dia" : "dias"}`;
    return { text: `Armado${days}, aguarda conclusão da implantação`, alert: false };
  }
  const prefix = s.kind === "NO_LIFECYCLE" ? "Estado do contrato não definido · " : "";
  const failure = s.lastFailedAt ? ` · a rodada de ${ddmmHHmm(s.lastFailedAt)} falhou` : "";
  if (s.stale || !s.lastRoundAt) {
    return { text: `${prefix}${s.lastRoundAt ? `Sem rodada desde ${ddmm(s.lastRoundAt)}` : "Nenhuma rodada registrada"}${failure}`, alert: true };
  }
  const since = s.since ? ` desde ${ddmm(s.since)}` : "";
  const idle = lastRoundIdle ? ", sem movimentações" : "";
  return {
    text: `${prefix}Ativo${since} · última rodada em ${ddmmHHmm(s.lastRoundAt)}${idle}${failure}`,
    alert: s.kind === "NO_LIFECYCLE" || !!s.lastFailedAt,
  };
}

/**
 * invariant: espelho de `INVALID_PARAMS_MESSAGE` do backend (`campaigns/monitor/fetch-campaign-monitor-use-case.ts`,
 * movida para `campaigns/monitor/unreadable.ts` pela #2265); mudar lá é mudar aqui.
 * why: com params inválidos o backend manda `typeState` OFF por reserva; a tela não pode dizer "desligada".
 * ponytail: casa pelo texto; quando a resposta do monitor trouxer o `estadoConhecido` da #2265, ler o campo.
 */
export const INVALID_PARAMS_MESSAGE = "Dados não lidos: os parâmetros da campanha deste contrato são inválidos; detalhe no log do servidor.";

export function contractStateText(c: Pick<ContractView, "typeState" | "lastRoundIdle" | "unreadable">): { text: string; alert: boolean } {
  if (c.unreadable === INVALID_PARAMS_MESSAGE) return { text: "Estado não determinado", alert: true };
  return typeStateText(c.typeState, c.lastRoundIdle);
}

const PREDICATES: Record<MonitorFilter, (r: QueueRow) => boolean> = {
  SEM_CANAL: (r) => r.withoutChannel,
  FALHA_ENVIO: (r) => !!r.sendError,
  MAIS_15_DIAS: (r) => (r.daysInCampaign ?? 0) > 15,
  SEM_RESPOSTA: (r) => r.reason === "SEM_RESPOSTA",
  FALTA_DEPENDENTE: (r) => r.reason === "FALTA_DEPENDENTE",
};

export const FILTER_LABELS: Record<MonitorFilter, string> = {
  SEM_CANAL: "Sem canal",
  FALHA_ENVIO: "Falha de envio",
  MAIS_15_DIAS: "Mais de 15 dias",
  SEM_RESPOSTA: "Sem resposta",
  FALTA_DEPENDENTE: "Falta dependente",
};

export function applyFilters(rows: QueueRow[], filters: MonitorFilter[]): QueueRow[] {
  return rows.filter((r) => filters.every((f) => PREDICATES[f](r)));
}

const PARAM_TO_FILTER: Record<string, MonitorFilter> = {
  "sem-canal": "SEM_CANAL",
  "falha-envio": "FALHA_ENVIO",
  "mais-15-dias": "MAIS_15_DIAS",
  "sem-resposta": "SEM_RESPOSTA",
  "falta-dependente": "FALTA_DEPENDENTE",
};

/** why: `?filtro=pendentes` (link do e-mail do RH, T11) é a fila inteira, que só tem pendentes. */
export function parseFilterParam(search: string): MonitorFilter[] {
  const raw = new URLSearchParams(search).get("filtro") ?? "";
  return raw
    .split(",")
    .map((v) => PARAM_TO_FILTER[v.trim()])
    .filter((f): f is MonitorFilter => !!f);
}

export const reasonText = (r: WaitingReason): string => FILTER_LABELS[r];

export const decisionText = (d: boolean | null): string => (d === true ? "Sim" : d === false ? "Não" : "Sem decisão");

export const OUTCOME_LABELS: Record<keyof MonitorOutcomes, string> = {
  SIM: "Respondeu sim",
  NAO: "Respondeu não",
  RH: "Decidido pelo RH",
  ENCERRADO_MANUAL: "Encerrado manualmente",
  NAO_NECESSARIO: "Deixou de ser necessário",
};

/** invariant: sem canal (inclusive comunicação da corretora desligada) o servidor recusa; o botão também. */
export function canResend(r: QueueRow): boolean {
  return !!r.primaryOccurrenceKey && !!r.email && !r.withoutChannel && r.manualResendsToday < MAX_MANUAL_RESENDS_PER_DAY;
}

/** invariant: o `top` do backend não conta o destino não lido (R8); quem mostra sem canal e falhas soma isto. */
export const destinationErrorCount = (d: Pick<CampaignMonitorResponse, "queue">): number =>
  d.queue.filter((r) => r.recipientSource === "ERROR").length;

export function pendingPeople(r: QueueRow): { taxPayerId: string; name: string }[] {
  return [
    ...(r.titularDecision === null ? [{ taxPayerId: r.taxPayerId, name: r.name }] : []),
    ...r.dependents.filter((d) => d.decision === null).map((d) => ({ taxPayerId: d.taxPayerId, name: d.name })),
  ];
}
